import { useState, useEffect } from "react";
import { parseUnits, etherUnits, Address } from "viem";
import { useAccount, useReadContract } from "wagmi";
import { contractAddresses, LockingManagerAbi, WstUSCAbi } from "@meta";
import {
  useWingsContractWrite,
  useNotificationContext,
  StandardNotifBody,
  TransactionComplete,
  Transfer,
  TransferMoney,
  DisplayErrorNotif,
  StepsContainer,
  ApproveComplete,
  FlexCol,
  InputField,
  Button,
  DisplayableInputField,
  SVGWrapper,
  FlexRow,
  InputSliderField,
  Typography,
} from "@shared";
import { displayTokens, formatMoney, formatUnitsToMaxValue } from "@utils";

import { SmallDropdown, DropdownItem } from "../../smallDropdown/SmallDropdown";

import { useButtonPropsOverride } from "../../../../../state/across/buttonPropsOverride";
import { useStepState } from "../../../../../state/common/useStepState.ui";
import { useFetchWstUscPrice } from "../../../../../state/wstUsc/useFetchWstUscPrice";

/* ----------- */
/*    Icons    */
/* ----------- */
import UscEth from "@assets/tokens/uscEth.svg";
import ChiEth from "@assets/tokens/chiEth.svg";
import WstUsc from "@assets/tokens/wstUsc.svg";
import BoostedWstUsc from "@assets/tokens/boostedWstUsc.svg";
import { useWingsApprove } from "../../../../../state/common/useWingsApprove";

interface WstUscBoostFormProps {
  selectedKey: string | null;
  onKeySelect: (key: string) => void;
}

export const WstUscBoostForm: React.FC<WstUscBoostFormProps> = ({
  selectedKey,
  onKeySelect,
}) => {
  const { address } = useAccount();

  //SLIDER
  const [sliderValue, setSliderValue] = useState<number>(5);
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderValue(Number(e.target.value));
  };

  const handleLockInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderValue(Number(e.target.value));
  };

  const items: DropdownItem[] = [
    { key: "stUscEth", icon: UscEth, name: "Staked USC/ETH LP", size: "large" },
    { key: "stChiEth", icon: ChiEth, name: "Staked CHI/ETH LP", size: "large" },
    { key: "wstUsc", icon: WstUsc, name: "wstUSC", size: "small" },
  ];

  const handleDropdownSelect = (item: DropdownItem) => {
    onKeySelect(item.key);
  };

  const { writeContractAsync: lockAsync, isPending: isStaking } =
    useWingsContractWrite();

  const [boostValue, setBoostValue] = useState("");

  const { approveAsync, isApproving, isApproved } = useWingsApprove(
    contractAddresses.WstUSC,
    contractAddresses.WST_USC_Locking,
    parseUnits(String(boostValue), etherUnits.wei)
  );

  const { showNotification } = useNotificationContext();
  const { steps, currentStep, finishSteps, setCurrentStep } = useStepState(
    ["Approve", "Boost"],
    isApproved ? 1 : 0
  );
  useEffect(() => {
    if (isApproved) {
      setCurrentStep(1);
    } else setCurrentStep(0);
  }, [isApproved]);

  const onBoostValueChange = (value: string) => {
    setBoostValue(value);
  };

  const lockFunction = async () => {
    await lockAsync(
      {
        abi: LockingManagerAbi,
        address: contractAddresses.WST_USC_Locking,
        functionName: "lock",
        args: [
          parseUnits(String(boostValue), etherUnits.wei),
          BigInt(sliderValue * 7),
        ],
      },
      {
        onSuccess: (txHash?: Address) => {
          finishSteps();
          showNotification({
            status: "success",
            txHash,
            content: (
              <StandardNotifBody
                headerComponent={<TransactionComplete />}
                transferComponent={
                  <Transfer
                    rightComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper
                            src={BoostedWstUsc}
                            width={22}
                            height={22}
                          />
                        }
                        text={formatMoney(boostValue)}
                        symbol="Boosted wstUSC"
                      />
                    }
                    leftComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper src={WstUsc} width={22} height={22} />
                        }
                        text={formatMoney(boostValue)}
                        symbol="wstUSC"
                      />
                    }
                  />
                }
              />
            ),
          });
          setBoostValue("");
        },
      }
    );
  };

  const loadingNotif = (currentStep: number) => {
    showNotification({
      status: "loading",
      content: (
        <StandardNotifBody
          header={"Boosting in Process"}
          loading
          transferComponent={
            <Transfer
              rightComponent={
                <TransferMoney
                  icon={
                    <SVGWrapper src={BoostedWstUsc} width={22} height={22} />
                  }
                  text={formatMoney(boostValue)}
                  symbol="Boosted wstUSC"
                />
              }
              leftComponent={
                <TransferMoney
                  icon={<SVGWrapper src={WstUsc} width={22} height={22} />}
                  text={formatMoney(boostValue)}
                  symbol="wstUSC"
                />
              }
            />
          }
          stepsComponent={
            <StepsContainer
              stepNames={steps}
              currentStep={currentStep}
              loading
            />
          }
        />
      ),
    });
  };

  const handleButtonClick = async () => {
    loadingNotif(currentStep);
    if (!isApproved) {
      await approveAsync(parseUnits(String(boostValue), etherUnits.wei), {
        onSuccess: async (txHash?: string) => {
          showNotification({
            txHash,
            status: "success",
            content: (
              <StandardNotifBody
                headerComponent={<ApproveComplete />}
                transferComponent={
                  <Transfer
                    rightComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper
                            src={BoostedWstUsc}
                            width={22}
                            height={22}
                          />
                        }
                        text={formatMoney(boostValue)}
                        symbol="Boosted wstUSC"
                      />
                    }
                    leftComponent={
                      <TransferMoney
                        icon={
                          <SVGWrapper src={WstUsc} width={22} height={22} />
                        }
                        text={formatMoney(boostValue)}
                        symbol="wstUSC"
                      />
                    }
                  />
                }
                stepsComponent={
                  <StepsContainer
                    stepNames={steps}
                    currentStep={currentStep}
                    loading
                  />
                }
              />
            ),
          });
          setCurrentStep(1);
          loadingNotif(1);
          await lockFunction();
        },
        onError: (message?: string) => {
          showNotification({
            status: "error",
            content: <DisplayErrorNotif message={message} />,
          });
        },
      });
    } else {
      await lockFunction();
    }
  };

  const { data: balance, isLoading: isMaxBalanceLoading } = useReadContract({
    abi: WstUSCAbi,
    address: contractAddresses.WstUSC,
    functionName: "balanceOf",
    args: [address as Address],
  });

  const { formattedPrice } = useFetchWstUscPrice();

  const valueDSPL: DisplayableInputField = {
    value: formatMoney(Number(boostValue || 0) * Number(formattedPrice || 0)),
    label: "Value: ",
  };

  const maxBalanceDSPL: DisplayableInputField = {
    value: displayTokens(balance || 0n, {}),
    isFetched: !isMaxBalanceLoading,
  };

  const { getButtonPropsOverride } = useButtonPropsOverride();

  return (
    <FlexCol className="gap-1">
      <div>
        <InputField
          name="wstUSCAmount"
          value={boostValue}
          onChange={(e) => onBoostValueChange(e.target.value)}
          label="You Boost"
          placeholder="0"
          rightLabel={
            <button
              className="bg-primary-10 text-primary rounded-md w-16 h-9 px-4 py-2 flex items-center justify-center text-body-small-medium"
              onClick={() => {
                onBoostValueChange(formatUnitsToMaxValue(balance)?.toString());
              }}
            >
              MAX
            </button>
          }
          dollarValue={valueDSPL}
          walletBalance={maxBalanceDSPL}
        />
      </div>

      <FlexRow className="justify-between items-center w-full">
        <SmallDropdown
          items={items}
          onSelect={handleDropdownSelect}
          selectedItem={items.find((item) => item.key === selectedKey)}
        />
      </FlexRow>

      <FlexCol className="w-full gap-4">
        <FlexCol className="w-full mt-3">
          <Typography
            type="body-medium-regular"
            className="text-neutral-black-60 mb-[-25px]"
          >
            Boost Period (weeks)
          </Typography>
          <FlexRow className="w-full gap-4 items-center">
            <InputSliderField
              min={1}
              max={52}
              value={sliderValue}
              onChange={handleSliderChange}
              width={240}
            />
            <InputField
              value={sliderValue}
              onChange={handleLockInputChange}
              className="w-[55px]"
              min={1}
              max={52}
            />
          </FlexRow>
        </FlexCol>

        <FlexRow className="w-full h-[1px] bg-neutral-black-30" />
        <FlexRow className="w-full justify-between items-center">
          <Typography
            type="body-small-regular"
            className="text-neutral-black-60"
          >
            Time Booster
          </Typography>
          <Typography type="body-medium-medium" className="text-primary">
            {(Number(sliderValue) / 52).toFixed(2)}x
          </Typography>
        </FlexRow>
        <FlexRow className="w-full h-[1px] bg-neutral-black-30" />
      </FlexCol>

      <Button
        className="mt-[8.98px] md:mt-[19px]"
        onClick={handleButtonClick}
        disabled={isStaking || isApproving || !boostValue}
        loading={isStaking || isApproving}
        fullWidth
        size="big"
        color="primary"
        {...getButtonPropsOverride(1)}
      >
        {Number(boostValue || 0) > 0 || Number(boostValue || 0) > 0
          ? isApproved
            ? "Boost"
            : "Approve"
          : "Enter amount"}
      </Button>
    </FlexCol>
  );
};
